/* global API_URL, dataLayer */
import _, { has as hasProperty } from 'lodash'
import { scroller } from 'react-scroll'
import { cookie } from 'reduxModules/utils/cookie'

import logger from 'components/utils/logger'
import { navHandlers } from 'reduxModules/app/dispatchers'
import { dispatchDisplayRecaptcha } from 'reduxModules/captcha/dispatchers'
import modalDispatchers from 'reduxModules/generic/modal/dispatchers'
import { initConfirmation } from 'reduxModules/confirmation'
import {
  openProfile,
  closeProfile,
  openRetentionDialogAction,
  closeRetentionDialogAction,
  acceptRetentionDiscount,
  types as userTypes,
  disableRetentionDialogSuccess,
  enableRetentionDialogSuccess
} from 'reduxModules/users'
import { cartItemsFetch } from 'reduxModules/cart'
import types from 'reduxModules/auth/types'
import { subscriptionSummaryFetch } from 'reduxModules/subscriptions'
import { clearRecaptchaToken, sendUTM } from 'reduxModules/app'
import { homeFetchCurrent, homeFetchCurrentMore } from 'reduxModules/home'
import getErrorMessage from 'reduxModules/utils/getErrorMessage'
import { navigate } from '../../../utils/navigation'
import pushGa4Event from '../../../utils/pushGa4Event'
import {
  captureGa4Identifiers,
  resetGa4Capture
} from '../../../utils/ga4Identifiers'

import {
  changePassword,
  forgotPassword,
  resetPassword,
  loginWithFacebook,
  loginWithMicrosoft,
  login,
  proxyLogin,
  openAthensLogin,
  signUp,
  cancelSubscription,
  clearSubscription,
  markSubscriptionCancellationPending,
  getUser,
  cancelFreeTrial,
  revokeFreeTrialPermission,
  updateGiftAvailability,
  deleteAccount,
  downgradeSubscription
} from './'
import notifications, {
  toastError,
  toastServerError,
  toastSuccess
} from '../../../global/notifications'

const sendUTMCallback = async (dispatch) => {
  const result = {}
  // TODO: change to let after fix the original problem
  // result = await retrieveAdData(dispatch)
  if (!_.isEmpty(result))
    dispatch(sendUTM(result.utm)).then(([_, res]) => {
      if (res.type === 'SET_UTM_SUCCESS')
        Object.keys(res.payload).forEach((value) => {
          dispatch(cookie(`utm_${value}`, null, { path: '/' }))
        })
    })
}
// SPEC-027 D2: refresh GA4 capture on login/logout transitions so the cached
// session attributes are current for subsequent events.
//
// Trade-off (review S-1): resetGa4Capture only clears the in-flight capture
// token, not the already-cached `captured` values, and the captureGa4Identifiers
// re-run below is fire-and-forget. So the immediate pushGa4Event for the login
// event itself still reads the previous client_id/session_id; only later events
// observe the refreshed triple. This is intentional: the browser's `_ga` cookie
// (client_id) does not change across login, and a stale-but-real session_id is
// more useful than the user_id fallback we'd get by fully clearing the cache.
const refreshGa4Capture = () => {
  resetGa4Capture()
  captureGa4Identifiers()
}

const afterLoginCallback = (
  dispatch,
  props,
  subscription = {},
  inCheckout = false,
  target = ''
) => {
  const location = props?.location
  const next = props?.next || location?.state?.next

  if (_.isNumber(subscription))
    dispatch(subscriptionSummaryFetch(subscription)())

  dispatch(cartItemsFetch())
  // google optimize event
  dataLayer.push({ isLoggedIn: true })

  if (next) navigate(next)
  else if (!inCheckout) {
    const isFullUrl = (url) => /^(http|https):\/\//.test(url)

    if (isFullUrl(target))
      // If the target is a full URL, use window.location to navigate
      window.location.href = target
    // If it's a relative path, use navigation utility
    else navigate(target || '/')
  }
}

export const dispatchLogin = (dispatch, data, props) => {
  dispatch(
    login(data.recaptcha)({ email: data.email, password: data.password })
  )
    .then(async (res) => {
      if (res[1].type === types.AUTH_LOGIN_SUCCESS) {
        refreshGa4Capture()
        pushGa4Event(
          'GTMEvent',
          {
            eventCategory: 'account-sign-in',
            eventAction: 'form-completion',
            eventLabel: 'email'
          },
          res[1].payload.user.id
        )

        const { isMobile, inCheckout } = props
        const subscription = _.has(res[1], 'payload.user.subscription')
          ? res[1].payload.user.subscription
          : {}

        if (
          inCheckout &&
          hasProperty(res[1].payload, 'user.role') &&
          res[1].payload.user.role === 'free_library_subscriber'
        ) {
          navigate('/')
          toastError(notifications.unableToPurchase)
        } else afterLoginCallback(dispatch, props, subscription, inCheckout)
        if (isMobile) scroller.scrollTo('shipTo', { offset: 64, smooth: true })
        toastSuccess(notifications.login)
        if (subscription) sendUTMCallback(dispatch)
      } else if (res[1]?.payload?.status === 412) {
        toastError(notifications.recaptcha)
        dispatchDisplayRecaptcha(dispatch)
      } else
        toastServerError(
          getErrorMessage(res[1].payload, notifications.login.error.message)
        )
    })
    .catch(() => {
      toastError(notifications.login)
    })
    .finally(() => dispatch(clearRecaptchaToken()))
}

export const dispatchProxyLogin = (dispatch, data, props) => {
  dispatch(proxyLogin({ ...data }))
    .then(async (res) => {
      if (res[1].type === types.AUTH_LOGIN_SUCCESS) {
        refreshGa4Capture()
        pushGa4Event(
          'GTMEvent',
          {
            eventCategory: 'account-sign-in',
            eventAction: 'form-completion',
            eventLabel: 'email'
          },
          res[1].payload.user.id
        )

        const { isMobile, inCheckout } = props
        const subscription = _.has(res[1], 'payload.user.subscription')
          ? res[1].payload.user.subscription
          : {}

        if (
          inCheckout &&
          hasProperty(res[1].payload, 'user.role') &&
          res[1].payload.user.role === 'free_library_subscriber'
        ) {
          navigate('/')
          toastError(notifications.unableToPurchase)
        } else afterLoginCallback(dispatch, props, subscription, inCheckout)
        if (isMobile) scroller.scrollTo('shipTo', { offset: 64, smooth: true })
        toastSuccess(notifications.login)
      } else toastError(notifications.login)
    })
    .catch((payload) => {
      const error = payload.errors
      toastServerError(error)
    })
}

export const dispatchSignUp = (dispatch, data, props) => {
  dispatch(
    signUp(data.recaptcha)({
      email: data.email,
      password1: data.password1
    })
  )
    .then(([_, res]) => {
      if (res.type === types.AUTH_SIGNUP_SUCCESS) {
        const { inCheckout, isMobile, location } = props
        refreshGa4Capture()
        pushGa4Event(
          'GTMEvent',
          {
            eventCategory: 'account-creation',
            eventAction: 'form-completion',
            eventLabel: 'email'
          },
          res.payload.user.id
        )
        toastSuccess(notifications.signup)

        if (hasProperty(location, 'query.forum'))
          window.location.href = `${API_URL}/accounts/login/?next=/forum/return-forum`
        else if (!inCheckout) navigate('/')
        else if (isMobile)
          scroller.scrollTo('shipTo', { offset: 64, smooth: true })
        dataLayer.push({ isLoggedIn: true })
      } else if (res?.payload?.status === 412) {
        toastError(notifications.recaptcha)
        dispatchDisplayRecaptcha(dispatch)
      } else
        toastServerError(
          getErrorMessage(res.payload, notifications.signup.error.message)
        )
    })
    .catch((payload) => {
      const error = payload.errors
      toastServerError(error)
    })
    .finally(() => dispatch(clearRecaptchaToken()))
}

export const dispatchFacebookLogin = (dispatch, accessToken, props) =>
  dispatch(loginWithFacebook({ access_token: accessToken })).then((res) => {
    if (res[1].type === types.AUTH_FACEBOOK_LOGIN_SUCCESS) {
      try {
        refreshGa4Capture()
        pushGa4Event(
          'GTMEvent',
          {
            eventCategory: props.text.includes('register')
              ? 'account-creation'
              : 'account-sign-in',
            eventAction: 'form-completion',
            eventLabel: 'facebook'
          },
          res[1].payload.user.id
        )
      } catch (e) {
        logger.error('An error occurred after user signed in using Facebook', {
          error: e
        })
        throw e
      }
      const subscription = _.has(res[1], 'payload.user.subscription')
        ? res[1].payload.user.subscription
        : {}
      const { inCheckout } = props
      afterLoginCallback(dispatch, props, subscription, inCheckout)
      toastSuccess(notifications.login)
      if (subscription) sendUTMCallback(dispatch)
    } else toastError(notifications.login)
  })

export const dispatchMicrosoftLogin = (dispatch, accessToken, props) =>
  dispatch(loginWithMicrosoft({ access_token: accessToken })).then((res) => {
    if (res[1].type === types.AUTH_MICROSOFT_LOGIN_SUCCESS) {
      try {
        refreshGa4Capture()
        pushGa4Event(
          'GTMEvent',
          {
            eventCategory: props.text.includes('register')
              ? 'account-creation'
              : 'account-sign-in',
            eventAction: 'form-completion',
            eventLabel: 'microsoft'
          },
          res[1].payload.user.id
        )
      } catch (e) {
        logger.error('An error occurred after user signed in using Microsoft', {
          error: e
        })
        throw e
      }
      const subscription = _.has(res[1], 'payload.user.subscription')
        ? res[1].payload.user.subscription
        : {}
      const { inCheckout } = props
      afterLoginCallback(dispatch, props, subscription, inCheckout)
      toastSuccess(notifications.login)
    } else toastError(notifications.login)
  })

export const dispatchForgotPassword = (dispatch, data) =>
  dispatch(forgotPassword(data)).then(([_, res]) => {
    if (res.type === types.AUTH_FORGOT_PASSWORD_SUCCESS) {
      toastSuccess(notifications.forgotPassword)
      navigate('/')
    } else toastError(notifications.forgotPassword)
  })

export const dispatchResetPassword = (dispatch, data, props) => {
  const payload = {
    new_password1: data.password1,
    new_password2: data.password2,
    token: props.params.token,
    uid: props.params.uid
  }
  dispatch(resetPassword(payload))
    .then(([_, res]) => {
      if (res.type === types.AUTH_RESET_PASSWORD_SUCCESS) {
        toastSuccess(notifications.passwordChange)
        navigate('/')
      } else toastError(notifications.passwordChange)
    })
    .catch((payload) => {
      const error = payload.errors
      toastServerError(error)
    })
}

export const dispatchChangePassword = (dispatch, data) => {
  const payload = {
    new_password1: data.password1,
    new_password2: data.password2,
    old_password: data.oldPassword
  }
  dispatch(changePassword(payload))
    .then(([_, res]) => {
      if (res.type === types.AUTH_CHANGE_PASSWORD_SUCCESS) {
        toastSuccess(notifications.passwordChange)
        navigate('/')
      } else toastError(notifications.passwordChange)
    })
    .catch((payload) => {
      const error = payload.errors
      toastServerError(error)
    })
}

export const onChangePasswordDispatch = (dispatch) => ({
  onSubmit: (data) => dispatchChangePassword(dispatch, data),
  ...navHandlers(dispatch)
})

export const onForgotPasswordDispatch = (dispatch) => ({
  onSubmit: (data) => dispatchForgotPassword(dispatch, data),
  ...navHandlers(dispatch)
})

export const onResetPasswordDispatch = (dispatch, props) => ({
  onSubmit: (data) => dispatchResetPassword(dispatch, data, props),
  ...navHandlers(dispatch)
})

export const onLoginBoxDispatch = (stateProps, dispatchProps, ownProps) => {
  const { dispatch } = dispatchProps
  return {
    ...stateProps,
    ...ownProps,
    onSubmit: (data) =>
      dispatchLogin(
        dispatch,
        { ...data, recaptcha: stateProps.recaptcha },
        ownProps
      )
  }
}

export const onAuthDispatch = (dispatch) => ({
  ...navHandlers(dispatch)
})

export const onSignupBoxDispatch = (stateProps, dispatchProps, ownProps) => {
  const { dispatch } = dispatchProps
  return {
    ...stateProps,
    ...ownProps,
    onSubmit: (data) =>
      dispatchSignUp(
        dispatch,
        { ...data, recaptcha: stateProps.recaptcha },
        ownProps
      )
  }
}

export const facebookLoginCallbackDispatch = (dispatch, props) => ({
  facebookLoginCallback: (response) =>
    dispatchFacebookLogin(dispatch, response.accessToken, props)
})

export const microsoftLoginCallbackDispatch = (dispatch, props) => ({
  microsoftLoginCallback: (response) =>
    dispatchMicrosoftLogin(dispatch, response.accessToken, props)
})

export const accountDispatch = (dispatch) => ({
  dispatchCancelSubscription: (subscription) => {
    const { cancellationDiscountEligible, cancellationDiscountPercentage } =
      subscription
    const confirmation = {
      title: 'Sorry to see you go',
      text: 'What makes you cancel?',
      options: [
        { resolveValue: 'accept', text: 'Continue', class: 'confirm-btn' }
      ],
      feedback: {
        options: [
          {
            text: 'Site speed too slow',
            value: 'Site speed too slow'
          },
          {
            text: 'Wanted to check us out',
            value: 'Wanted to check us out'
          },
          {
            text: "Can't afford it",
            value: "Can't afford it"
          },
          {
            text: 'Not enough value',
            value: 'Not enough value'
          },
          {
            text: 'Not enough time',
            value: 'Not enough time'
          },
          {
            text: 'Technical issue',
            value: 'Technical issue'
          },
          {
            text: 'Subscription plan upgrade',
            value: 'Subscription plan upgrade'
          },
          {
            text: 'Subscription plan downgrade',
            value: 'Subscription plan downgrade'
          },
          {
            text: 'Not using',
            value: 'Not using'
          },
          {
            text: 'Used what I needed',
            value: 'Used what I needed'
          },
          {
            text: 'No longer working in the field',
            value: 'No longer working in the field'
          },
          {
            text: 'Not what I thought I was getting',
            value: 'Not what I thought I was getting'
          },
          {
            text: 'Free trial ended',
            value: 'Free trial ended'
          },
          {
            text: 'Not enough courses in my field',
            value: 'Not enough courses in my field'
          },
          {
            text: 'Other',
            value: 'Other'
          }
        ]
      },
      height: 472,
      componentId: 'cancellation'
    }
    dispatch(initConfirmation(confirmation)).on('accept', (feedback) => {
      if (cancellationDiscountEligible && cancellationDiscountPercentage > 0)
        dispatch(openRetentionDialogAction())
      else if (subscription.discountPercentage > 0)
        dispatch(openRetentionDialogAction())
      else
        dispatch(
          cancelSubscription({ ...(!_.isEmpty(feedback) && { feedback }) })
        )
          .then(([_, res]) => {
            if (res.type === types.AUTH_CANCEL_SUBSCRIPTION_SUCCESS) {
              if (!res.payload.pendingChange) dispatch(clearSubscription())
              else dispatch(markSubscriptionCancellationPending())
              toastSuccess(notifications.cancelSubscription)
            } else toastError(notifications.cancelSubscription)
          })
          .catch(() => toastError(notifications.cancelSubscription))
    })
  },
  dispatchDirectCancelSubscription: () => {
    dispatch(cancelSubscription())
      .then(([_, res]) => {
        if (res.type === types.AUTH_CANCEL_SUBSCRIPTION_SUCCESS) {
          if (!res.payload.pendingChange) dispatch(clearSubscription())
          else dispatch(markSubscriptionCancellationPending())
          toastSuccess(notifications.cancelSubscription)
        } else toastError(notifications.cancelSubscription)
      })
      .catch(() => toastError(notifications.cancelSubscription))
  },
  dispatchCancelFreeTrial: (forPromo = false, usedFreeTrial = false) => {
    const regularCancelationCopy = {
      title: 'Cancel free trial',
      text: 'This will charge your payment method, do you want to cancel your free trial?',
      options: [
        { resolveValue: 'accept', text: 'Continue', class: 'confirm-btn' }
      ]
    }
    const promoCancellationCopy = {
      title: 'Payment confirmation',
      text: 'This will charge your payment method. Do you want to pay for your subscription to be able to gift a matching subscription?',
      options: [
        { resolveValue: 'accept', text: 'Confirm', class: 'confirm-btn' }
      ]
    }
    const confirmation = forPromo
      ? promoCancellationCopy
      : regularCancelationCopy

    if (!usedFreeTrial)
      dispatch(initConfirmation(confirmation)).on('accept', () =>
        dispatch(cancelFreeTrial())
          .then(([_, res]) => {
            if (res.type === types.CANCEL_FREE_TRIAL_SUCCESS) {
              dispatch(revokeFreeTrialPermission())
              toastSuccess(notifications.freeTrialCancelled)
              if (forPromo) {
                toastSuccess(notifications.enabledToGift)
                dispatch(updateGiftAvailability())
                navigate('/gift_subscription')
              }
            } else toastError(notifications.freeTrialCancelled)
          })
          .catch((payload) => {
            const error = payload.errors
            toastServerError(error)
          })
      )
    else navigate('/gift_subscription')
  },
  ...modalDispatchers(dispatch),
  openProfileSection() {
    dispatch(openProfile())
  },
  closeProfileSection() {
    dispatch(closeProfile())
  },
  downgradeSubscription() {
    dispatch(downgradeSubscription({ downgrade: true }))
      .then(([_, res]) => {
        if (res.type === types.AUTH_DOWNGRADE_SUBSCRIPTION_SUCCESS) {
          dispatch(getUser())
          toastSuccess(notifications.downgradeSubscription)
        } else toastError(notifications.downgradeSubscription)
      })
      .catch(() => toastError(notifications.downgradeSubscription))
  },
  closeRetentionDialog() {
    dispatch(closeRetentionDialogAction())
  },
  acceptRetentionDiscount: () => {
    dispatch(disableRetentionDialogSuccess())
    dispatch(acceptRetentionDiscount())
      .then(([_, res]) => {
        if (
          res.type === userTypes.USERS_PROFILE_ACCEPT_RETENTION_DISCOUNT_SUCCESS
        ) {
          toastSuccess(notifications.acceptRetentionDiscount)
          dispatch(closeRetentionDialogAction())
        } else toastError(notifications.acceptRetentionDiscount)
      })
      .catch((payload) => {
        const error = payload.errors
        toastServerError(error)
      })
      .finally(() => dispatch(enableRetentionDialogSuccess()))
  }
})

export const accountMenuDispatcher = (dispatch) => ({
  getUserInfo: () => dispatch(getUser())
})

export const WATCH_HISTORY_PAGE_SIZE = 12

export const watchHistoryDispatcher = (dispatch) => ({
  loadWatchHistory() {
    dispatch(
      homeFetchCurrent({
        auto_generated: true,
        full: true,
        ordering: '-id',
        limit: WATCH_HISTORY_PAGE_SIZE
      })()
    )
  },
  loadMoreWatchHistory(offset) {
    dispatch(
      homeFetchCurrentMore({
        auto_generated: true,
        full: true,
        ordering: '-id',
        limit: WATCH_HISTORY_PAGE_SIZE,
        offset
      })()
    )
  }
})

export const dispatchOpenAthensLogin = (dispatch, data, props) => {
  dispatch(openAthensLogin({ ...data }))
    .then(async (res) => {
      const target = props.location?.query?.target
        ? decodeURIComponent(props.location?.query?.target)
        : ''
      if (res[1].type === types.AUTH_LOGIN_SUCCESS) {
        refreshGa4Capture()
        pushGa4Event(
          'GTMEvent',
          {
            eventCategory: 'account-sign-in',
            eventAction: 'form-completion',
            eventLabel: 'email'
          },
          res[1].payload.user.id
        )

        const { isMobile, inCheckout } = props
        const subscription = _.has(res[1], 'payload.user.subscription')
          ? res[1].payload.user.subscription
          : {}

        if (
          inCheckout &&
          hasProperty(res[1].payload, 'user.role') &&
          res[1].payload.user.role === 'free_library_subscriber'
        ) {
          navigate('/')
          toastError(notifications.unableToPurchase)
        } else
          afterLoginCallback(dispatch, props, subscription, inCheckout, target)
        if (isMobile) scroller.scrollTo('shipTo', { offset: 64, smooth: true })
        toastSuccess(notifications.login)
      } else toastError(notifications.login)
    })
    .catch((payload) => {
      const error = payload.errors
      toastServerError(error)
    })
}

export const deleteAccountDispatch = (dispatch) => ({
  deleteAccountAction: () => {
    dispatch(deleteAccount())
      .then(([_, res]) => {
        if (res.type === types.AUTH_ACCOUNT_DELETE_SUCCESS) {
          toastSuccess(notifications.deleteAccount)
          dataLayer.push({ isLoggedIn: false })
          refreshGa4Capture()
          navigate('/')
        } else toastError(notifications.deleteAccount)
      })
      .catch((payload) => {
        const error = payload.errors
        toastServerError(error)
      })
  }
})
